// Fields tab — module selector + FieldTable + bulk actions.
// 'Apply Template' bulk action opens TemplateDiffPanel in a dialog.

import { useState } from "react";
import type { JSX } from "react";
import { toast } from "sonner";
import type { TenantConfig, ModuleKey } from "../lib/adminApiClient.js";
import { toggleField, applyTemplate, listTemplates } from "../lib/adminApiClient.js";
import type { ConfigTemplate } from "../lib/adminApiClient.js";
import { FIELD_REGISTRY } from "@hcm/core";
import { MODULE_META, MODULE_ORDER } from "../lib/modulesMeta.js";
import { FieldTable } from "./FieldTable.js";
import { TemplateDiffPanel } from "./TemplateDiffPanel.js";
import type { TemplateDiff } from "../lib/adminApiClient.js";

interface Props { tenantId: string; config: TenantConfig; }

const SEL = "rounded-lg border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500";
const BTN = "px-3 py-1.5 text-xs font-medium rounded-lg transition-colors";

// eslint-disable-next-line max-lines-per-function -- top-level page component composes 4 panels; splitting hurts readability
export function FieldsTab({ tenantId, config }: Props): JSX.Element {
  const [moduleKey, setModuleKey] = useState<ModuleKey>("visit_reports");
  const [applyOpen, setApplyOpen] = useState(false);
  const [templates, setTemplates] = useState<ConfigTemplate[]>([]);
  const [selectedTpl, setSelectedTpl] = useState<string>("");
  const [diff, setDiff] = useState<TemplateDiff | null>(null);
  const [applying, setApplying] = useState(false);

  const registry = FIELD_REGISTRY[moduleKey] ?? {};
  const fieldKeys = Object.keys(registry);
  const currentFields = config.fields?.[moduleKey] ?? {};

  async function activateAll(): Promise<void> {
    await Promise.all(
      fieldKeys.map((k) => {
        const f = currentFields[k];
        if (!f?.locked) return toggleField(tenantId, moduleKey, k, true, f?.required ?? false);
      }),
    );
    toast.success("All fields activated.");
  }

  async function deactivateNonRequired(): Promise<void> {
    await Promise.all(
      fieldKeys.map((k) => {
        const f = currentFields[k];
        if (!f?.locked && !f?.required) return toggleField(tenantId, moduleKey, k, false, false);
      }),
    );
    toast.success("Non-required fields deactivated.");
  }

  async function openApplyTemplate(): Promise<void> {
    const tpls = await listTemplates();
    setTemplates(tpls);
    setSelectedTpl(tpls[0]?._id ?? "");
    setDiff(null);
    setApplyOpen(true);
  }

  async function previewDiff(tplId: string): Promise<void> {
    if (!tplId) return;
    setSelectedTpl(tplId);
    const d = await applyTemplate(tenantId, tplId, true);
    setDiff(d as TemplateDiff);
  }

  async function confirmApply(): Promise<void> {
    if (!selectedTpl) return;
    setApplying(true);
    try {
      await applyTemplate(tenantId, selectedTpl, false);
      toast.success("Template applied successfully.");
      setApplyOpen(false);
    } catch {
      toast.error("Failed to apply template.");
    } finally {
      setApplying(false);
    }
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-3">
        <select value={moduleKey} onChange={(e) => setModuleKey(e.target.value as ModuleKey)} className={SEL}>
          {MODULE_ORDER.map((k) => (
            <option key={k} value={k}>{MODULE_META[k].label}</option>
          ))}
        </select>
        <div className="ml-auto flex gap-2 flex-wrap">
          <button onClick={() => void activateAll()} className={`${BTN} bg-green-50 text-green-700 hover:bg-green-100`}>
            Activate All
          </button>
          <button onClick={() => void deactivateNonRequired()} className={`${BTN} bg-red-50 text-red-700 hover:bg-red-100`}>
            Deactivate Non-Required
          </button>
          <button onClick={() => void openApplyTemplate()} className={`${BTN} bg-indigo-50 text-indigo-700 hover:bg-indigo-100`}>
            Apply Template
          </button>
        </div>
      </div>

      <FieldTable tenantId={tenantId} moduleKey={moduleKey} fields={currentFields} />

      {applyOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-lg p-6 space-y-4 max-h-[90vh] overflow-y-auto">
            <h3 className="text-base font-semibold text-slate-900">Apply Template</h3>
            <select value={selectedTpl} onChange={(e) => void previewDiff(e.target.value)} className={`w-full ${SEL}`}>
              {templates.map((t) => <option key={t._id} value={t._id}>{t.template_name}</option>)}
            </select>
            {diff && <TemplateDiffPanel diff={diff} />}
            <div className="flex justify-end gap-2 pt-2">
              <button onClick={() => setApplyOpen(false)} className="px-3 py-1.5 text-sm rounded-lg bg-slate-100 text-slate-600 hover:bg-slate-200">Cancel</button>
              <button onClick={() => void confirmApply()} disabled={!diff || applying} className="px-3 py-1.5 text-sm rounded-lg bg-indigo-600 text-white hover:bg-indigo-700 disabled:opacity-50">
                {applying ? "Applying…" : "Apply"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
